// ================================================================
// Picker Modal — Make-style "Add a module"
// ================================================================
// Layout: sidebar چپ (categories + feedback)
//         + main راست (search bar بالا + لیست apps پایین)

import { t } from '../../i18n/index.js';
import { icon, escapeHtml } from '../../utils/dom.js';
import { state } from '../../core/state.js';
import { events, EVENTS } from '../../core/events.js';
import { nodeRegistry } from '../../nodes/registry.js';

let backdropEl = null;
let fromNodeId = null;
let dropPosition = null;
let activeCategory = 'allApps';
let searchQuery = '';

const CATEGORIES = [
  { key: 'allApps', labelKey: 'picker.categories.allApps', icon: 'apps' },
  { key: 'builtIn', labelKey: 'picker.categories.builtIn', icon: 'build' }
];

export function initPickerModal(root) {
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop hidden';
  backdrop.id = 'picker-backdrop';
  root.appendChild(backdrop);
  backdropEl = backdrop;

  // کلیک بیرون → بستن
  backdrop.addEventListener('mousedown', (e) => {
    if (e.target === backdrop) close();
  });

  events.on(EVENTS.MODAL_OPEN_PICKER, (payload) => open(payload));
}

function open(payload) {
  if (payload && typeof payload === 'object') {
    fromNodeId = payload.fromNodeId ?? null;
    dropPosition = (payload.x != null && payload.y != null)
      ? { x: payload.x, y: payload.y }
      : null;
  } else {
    fromNodeId = payload ?? null;
    dropPosition = null;
  }
  activeCategory = 'allApps';
  searchQuery = '';
  renderFull();
  backdropEl.classList.remove('hidden');
  // فوکوس روی search
  setTimeout(() => {
    backdropEl.querySelector('#picker-search-input')?.focus();
  }, 50);
}

function close() {
  backdropEl.classList.add('hidden');
  fromNodeId = null;
  dropPosition = null;
  searchQuery = '';
}

// ─────────────────────────────────────────────────────────
// رندر کامل (یکبار وقتی modal باز می‌شه)
// ─────────────────────────────────────────────────────────
function renderFull() {
  backdropEl.innerHTML = `
    <div class="modal modal-picker-new">

      <!-- sidebar چپ -->
      <aside class="picker-sidebar">
        <nav class="picker-categories" id="picker-categories">
          ${CATEGORIES.map(cat => `
            <button class="picker-category ${cat.key === activeCategory ? 'active' : ''}" data-cat="${cat.key}">
              ${icon(cat.icon, 18)}
              <span>${escapeHtml(t(cat.labelKey))}</span>
            </button>
          `).join('')}
        </nav>

        <div class="picker-feedback">
          ${icon('chat_bubble_outline', 16)}
          <span>${escapeHtml(t('common.shareFeedback'))}</span>
        </div>
      </aside>

      <!-- main راست -->
      <section class="picker-main">

        <!-- نوار بالا: search + close -->
        <div class="picker-main-top">
          <div class="picker-search">
            <span class="picker-search-icon">${icon('search', 18)}</span>
            <input
              type="text"
              class="picker-search-input"
              placeholder="${escapeHtml(t('picker.searchPlaceholder'))}"
              value="${escapeHtml(searchQuery)}"
              id="picker-search-input"
              autocomplete="off"
              spellcheck="false"
            />
          </div>
          <button class="picker-close-btn" id="picker-close-btn" title="${t('modal.close')}">
            ${icon('close', 20)}
          </button>
        </div>

        <!-- لیست apps (این بخش تنها چیزیه که با search re-render می‌شه) -->
        <div class="picker-results" id="picker-results">
          ${renderResults()}
        </div>

      </section>
    </div>
  `;

  attachAllHandlers();
}

// ─────────────────────────────────────────────────────────
// رندر فقط بخش نتایج (با تغییر search یا category)
// ─────────────────────────────────────────────────────────
function renderResults() {
  const items = getFilteredItems();
  const categoryDef = CATEGORIES.find(c => c.key === activeCategory);
  const categoryLabel = categoryDef ? t(categoryDef.labelKey) : '';

  if (items.length === 0) {
    return `
      <div class="picker-main-header">
        <h3>${escapeHtml(categoryLabel)}</h3>
      </div>
      <div class="picker-empty">
        ${icon('search_off', 32)}
        <p>${t('picker.noResults')}</p>
      </div>
    `;
  }

  return `
    <div class="picker-main-header">
      <h3>${escapeHtml(categoryLabel)}</h3>
    </div>
    <div class="picker-app-list">
      ${items.map(def => `
        <button class="picker-app-row" data-type="${escapeHtml(def.type)}">
          <div class="picker-app-icon" style="background: ${def.colorGradient || def.color}; color: ${def.iconColor || 'white'}">
            ${icon(def.icon, 22)}
          </div>
          <span class="picker-app-name">${escapeHtml(t(def.nameKey))}</span>
        </button>
      `).join('')}
    </div>
  `;
}

function refreshResults() {
  const container = backdropEl.querySelector('#picker-results');
  if (!container) return;
  container.innerHTML = renderResults();
  attachAppRowHandlers();
}

// ─────────────────────────────────────────────────────────
// فیلتر آیتم‌ها بر اساس category + search
// ─────────────────────────────────────────────────────────
function getFilteredItems() {
  let items;

  // اگه search داره، در کل apps جستجو کن (ignore category)
  if (searchQuery.trim()) {
    items = nodeRegistry.getPickerItems();
    const q = searchQuery.toLowerCase();
    items = items.filter(def => {
      const name = t(def.nameKey).toLowerCase();
      const sub = t(def.subtitleKey).toLowerCase();
      return name.includes(q) || sub.includes(q);
    });
  } else {
    // وگرنه فقط category فعال
    items = nodeRegistry.getByCategory(activeCategory);
  }

  return items;
}

// ─────────────────────────────────────────────────────────
// Handlerها
// ─────────────────────────────────────────────────────────
function attachAllHandlers() {
  // close button
  backdropEl.querySelector('#picker-close-btn')?.addEventListener('click', close);

  // categories
  backdropEl.querySelectorAll('.picker-category').forEach(btn => {
    btn.addEventListener('click', () => {
      activeCategory = btn.dataset.cat;
      // update active state در DOM (بدون re-render کل sidebar)
      backdropEl.querySelectorAll('.picker-category').forEach(b => {
        b.classList.toggle('active', b.dataset.cat === activeCategory);
      });
      refreshResults();
    });
  });

  // search input — فقط نتایج رو دوباره می‌سازه، input دست‌نخورده می‌مونه
  const searchInput = backdropEl.querySelector('#picker-search-input');
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      searchQuery = e.target.value;
      refreshResults();
    });
  }

  // app rows
  attachAppRowHandlers();
}

function attachAppRowHandlers() {
  backdropEl.querySelectorAll('.picker-app-row').forEach(row => {
    row.addEventListener('click', () => {
      addNodeFromPicker(row.dataset.type);
    });
  });
}

// ─────────────────────────────────────────────────────────
// اضافه کردن node
// ─────────────────────────────────────────────────────────
function addNodeFromPicker(type) {
  const def = nodeRegistry.get(type);
  if (!def) return;

  // dummy ها فعلاً چیزی اضافه نمی‌کنن
  if (def.dummy) {
    console.log(`[Picker] "${t(def.nameKey)}" is a dummy node — not implemented yet.`);
    close();
    return;
  }

  if (fromNodeId !== null) {
    const from = state.getNode(fromNodeId);
    if (from) {
      const newNode = state.addNode(type, from.x + 180, from.y);
      state.addConnection(from.id, newNode.id);
    }
  } else if (dropPosition) {
    state.addNode(type, dropPosition.x - 45, dropPosition.y - 40);
  } else {
    state.addNode(type, 200 + Math.random() * 200, 200 + Math.random() * 100);
  }
  close();
}
